import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Heart, Sparkles, HandHeart, MessageCircle, Eye, Send,
  Store, Plus, Lock, ArrowRight, Image as ImageIcon, AtSign,
  Volume2, Pause, Play, Square, Search, X, Filter, ClipboardList,
  Calendar, MapPin, Car,
} from "lucide-react";

const REACTIONS = [
  { id: "love",     emoji: "❤️", label: "J'adore",   icon: Heart },
  { id: "applause", emoji: "👏", label: "Bravo",    icon: Sparkles },
  { id: "support",  emoji: "🤝", label: "Soutien",  icon: HandHeart },
];

// Render comment text with @mentions visually highlighted
function renderMentionText(text) {
  if (!text) return null;
  const parts = text.split(/(@[A-Za-z0-9_\u00C0-\u017F.-]{2,40})/g);
  return parts.map((part, i) =>
    part.startsWith("@") ? (
      <span key={i} className="text-pink-600 font-semibold bg-pink-50 px-1 rounded">
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

/**
 * Merchant social wall — positive-only social feed for Istres shops.
 */
export default function MerchantWallSection() {
  const { user } = useAuth();
  const isAuthed = !!user && user !== false;
  const isMerchant = isAuthed && user.role === "commercant";
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCompose, setOpenCompose] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => {
    try { return JSON.parse(localStorage.getItem("istres.followed_merchants") || "[]"); }
    catch { return []; }
  });
  const [filterOpen, setFilterOpen] = useState(false);

  const load = () => {
    api.get("/posts/").then((r) => setPosts(r.data || [])).catch(() => setPosts([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadMerchantsOnce().then(setMerchants); }, []);
  useEffect(() => {
    try { localStorage.setItem("istres.followed_merchants", JSON.stringify(selected)); } catch { /* ignore */ }
  }, [selected]);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  // Apply filters: text query (matches merchant name + post text) and selected merchant ids
  const filteredPosts = posts.filter((p) => {
    if (selected.length && !selected.includes(p.merchant_user_id)) return false;
    if (query.trim()) {
      const q = query.toLowerCase();
      const hay = `${p.merchant_name || ""} ${p.merchant_category || ""} ${p.text || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <section id="mur" className="bg-cream-50 scroll-mt-32" data-testid="merchant-wall">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="text-center mb-6">
          <div className="phenix-eyebrow mb-3 inline-flex items-center gap-1.5">
            <Store size={11} strokeWidth={2.5} /> Mur des commerçants
          </div>
          <h2 className="heading-display text-[1.8rem] sm:text-[2.4rem] text-plum-900 leading-[1.1]">
            Fil <span className="text-pink-500">d&apos;actu</span> des commerçants Istréens.
          </h2>
          <p className="mt-3 text-[15px] text-plum-700/70 max-w-xl mx-auto">
            Nouveautés, coulisses, coups de cœur : suivez l&apos;actualité de vos commerçants préférés. Seules les réactions bienveillantes y sont autorisées.
          </p>
        </div>

        {/* ===== Daily summary card (audio + large text) ===== */}
        <DailySummaryCard />

        {/* ===== Filter toolbar ===== */}
        <div className="phenix-card bg-white p-3 mb-4" data-testid="wall-filter-bar">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-plum-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher un commerçant ou un sujet…"
                className="w-full pl-9 pr-9 py-2.5 rounded-full bg-cream-50 border border-cream-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-plum-200"
                data-testid="wall-search"
              />
              {query && (
                <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-plum-400 hover:text-plum-700" aria-label="Effacer">
                  <X size={14} />
                </button>
              )}
            </div>
            <button
              onClick={() => setFilterOpen((v) => !v)}
              data-testid="wall-filter-toggle"
              className={`shrink-0 rounded-full px-3 h-10 inline-flex items-center gap-1.5 text-[12px] font-bold transition-colors ${
                filterOpen || selected.length ? "bg-plum-700 text-white" : "bg-cream-100 text-plum-700 hover:bg-cream-200"
              }`}
            >
              <Filter size={13} />
              {selected.length > 0 ? `${selected.length} suivi${selected.length > 1 ? "s" : ""}` : "Mes commerçants"}
            </button>
          </div>

          {filterOpen && (
            <div className="mt-3 border-t border-cream-100 pt-3" data-testid="wall-filter-panel">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-plum-700/60">Cochez les commerçants à suivre</span>
                {selected.length > 0 && (
                  <button onClick={() => setSelected([])} className="text-[11px] font-semibold text-pink-600 hover:text-pink-700" data-testid="wall-filter-clear">
                    Tout désélectionner
                  </button>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-1.5 max-h-60 overflow-y-auto">
                {merchants.map((m) => {
                  const isOn = selected.includes(m.id);
                  return (
                    <label
                      key={m.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer transition-colors ${isOn ? "bg-plum-50 border border-plum-200" : "hover:bg-cream-50 border border-transparent"}`}
                      data-testid={`wall-filter-merchant-${m.pseudo}`}
                    >
                      <input
                        type="checkbox"
                        checked={isOn}
                        onChange={() => toggle(m.id)}
                        className="w-4 h-4 rounded accent-plum-700 shrink-0"
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block font-display font-bold text-plum-900 text-[12px] truncate">{m.merchant_name || m.pseudo}</span>
                        <span className="block text-[10px] text-plum-700/55 truncate">{m.merchant_category || "Commerçant"}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Compose button (merchants only) */}
        {isMerchant && (
          <button
            onClick={() => setOpenCompose(true)}
            data-testid="post-create-btn"
            className="w-full mb-4 phenix-card bg-white p-4 flex items-center gap-3 hover:shadow-md transition-shadow text-left"
          >
            <div className="w-10 h-10 rounded-full bg-plum-100 text-plum-600 grid place-items-center shrink-0">
              <Plus size={18} strokeWidth={2.4} />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-plum-900 text-sm">Partagez avec vos clients…</div>
              <div className="text-[11px] text-plum-700/65">Nouveauté, événement, coup de cœur · photo ou vidéo</div>
            </div>
            <ArrowRight size={15} className="text-plum-400" />
          </button>
        )}

        {/* Feed */}
        {loading ? (
          <div className="phenix-card p-10 animate-pulse bg-white" />
        ) : filteredPosts.length === 0 ? (
          <div className="phenix-card bg-white p-10 text-center" data-testid="wall-empty">
            <Store size={28} className="mx-auto text-plum-700/30 mb-2" />
            <h3 className="font-display font-bold text-plum-900">
              {posts.length === 0 ? "Le mur attend les premières publications" : "Aucun post ne correspond à votre filtre"}
            </h3>
            <p className="text-[13px] text-plum-700/65 mt-1 max-w-sm mx-auto">
              {posts.length === 0
                ? (isMerchant ? "Vous êtes commerçant ? Publiez votre premier post pour démarrer la conversation." : "Les commerçants d'Istres publieront ici très bientôt.")
                : "Essayez d'élargir vos filtres ou de modifier votre recherche."}
            </p>
            {posts.length > 0 && (selected.length > 0 || query) && (
              <button
                onClick={() => { setSelected([]); setQuery(""); }}
                className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-plum-700 hover:bg-plum-800 text-white px-5 py-2.5 text-[13px] font-bold"
                data-testid="wall-reset-filters"
              >
                Réinitialiser les filtres
              </button>
            )}
            {posts.length === 0 && !isAuthed && (
              <Link to="/login" className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-plum-700 hover:bg-plum-800 text-white px-5 py-2.5 text-[13px] font-bold">
                Se connecter <ArrowRight size={13} />
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-3" data-testid="wall-feed">
            {filteredPosts.map((p) => <PostCard key={p.id} post={p} isAuthed={isAuthed} onChange={load} />)}
          </div>
        )}
      </div>

      <ComposePostDialog open={openCompose} onClose={() => { setOpenCompose(false); load(); }} />
    </section>
  );
}

function PostCard({ post, isAuthed, onChange }) {
  const { user } = useAuth();
  const commentsCount = post.comments?.length || 0;
  const [showComments, setShowComments] = useState(commentsCount > 0);
  const myReaction = isAuthed && post.reactions?.find((r) => r.user_id === String(user.id))?.type;

  const react = async (type) => {
    if (!isAuthed) return toast.error("Connectez-vous pour réagir");
    try {
      await api.post(`/posts/${post.id}/react`, { type });
      window.dispatchEvent(new CustomEvent("istres-action-completed", { detail: { kind: "reaction" } }));
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reactionsByType = (type) => post.reactions?.filter((r) => r.type === type).length || 0;
  const totalReactions = post.reactions?.length || 0;

  const date = new Date(post.created_at);
  const fmt = date.toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  return (
    <article className="phenix-card bg-white overflow-hidden" data-testid={`post-${post.id}`}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 pt-4">
        {post.merchant_logo ? (
          <img src={post.merchant_logo} alt="" className="w-11 h-11 rounded-full object-cover border-2 border-cream-100 shrink-0" />
        ) : (
          <div className="w-11 h-11 rounded-full bg-sun-100 text-sun-700 grid place-items-center font-display font-extrabold text-base shrink-0">
            {(post.merchant_name || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-plum-900 text-[14px] truncate">{post.merchant_name}</div>
          <div className="text-[11px] text-plum-700/55">{post.merchant_category && `${post.merchant_category} · `}{fmt}</div>
        </div>
        <button
          onClick={() => {
            const evt = new CustomEvent("istres-assistant-open", {
              detail: {
                prefill: `Parle-moi de ${post.merchant_name}${post.merchant_category ? ` (${post.merchant_category})` : ""} à Istres`,
                autoSend: true,
              },
            });
            window.dispatchEvent(evt);
          }}
          data-testid={`ask-elise-${post.id}`}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-gradient-to-r from-pink-50 to-sun-50 border border-pink-200 hover:border-pink-400 text-pink-700 text-[11px] font-bold transition-colors group"
          title={`Demander à Élise des infos sur ${post.merchant_name}`}
        >
          <Sparkles size={11} strokeWidth={2.5} className="group-hover:rotate-12 transition-transform" />
          Élise
        </button>
      </div>

      {/* Body */}
      <p className="px-4 py-3 text-[15px] text-plum-900 leading-relaxed whitespace-pre-line">{post.text}</p>

      {/* Attached survey CTA */}
      {post.attached_survey_id && (
        <Link
          to={`/sondages/${post.attached_survey_id}`}
          className="mx-4 mb-3 flex items-center gap-3 rounded-2xl border-2 border-sun-300 bg-gradient-to-br from-sun-50 to-pink-50 p-3.5 hover:border-pink-400 transition-colors group"
          data-testid={`post-attached-survey-${post.id}`}
        >
          <div className="w-11 h-11 rounded-xl bg-sun-400 text-plum-900 grid place-items-center shrink-0 shadow-md">
            <ClipboardList size={20} strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-pink-600 mb-0.5">🗳 Sondage du commerçant</div>
            <div className="font-display font-bold text-[14px] text-plum-900 leading-tight">
              Répondez en 30 sec, gagnez un bon de réduction
            </div>
          </div>
          <ArrowRight size={18} className="text-plum-700/50 group-hover:text-pink-500 group-hover:translate-x-0.5 transition-all shrink-0" />
        </Link>
      )}

      {/* Attached event card with nearest parking */}
      {post.attached_event && <AttachedEventCard ev={post.attached_event} postId={post.id} />}

      {/* Media: video if present, else photo */}
      {post.video_url ? (
        <PostMedia video={post.video_url} photo={post.photo_url} />
      ) : post.photo_url ? (
        <img src={post.photo_url} alt="" className="w-full max-h-[480px] object-cover" loading="lazy" />
      ) : null}

      {/* Stats */}
      <div className="px-4 py-2.5 flex items-center justify-between border-t border-cream-100 text-[11px] text-plum-700/55">
        <div className="flex items-center gap-3">
          {totalReactions > 0 && (
            <span className="inline-flex items-center gap-1">
              {REACTIONS.filter((r) => reactionsByType(r.id) > 0).slice(0, 3).map((r) => (
                <span key={r.id} aria-hidden="true">{r.emoji}</span>
              ))}
              <span className="font-bold tabular-nums">{totalReactions}</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowComments((v) => !v)}
            className="inline-flex items-center gap-1 hover:text-plum-700 transition-colors"
            data-testid={`post-comments-count-${post.id}`}
          >
            <MessageCircle size={11} /> {commentsCount} {commentsCount > 1 ? "commentaires" : "commentaire"}
          </button>
          <span className="inline-flex items-center gap-1"><Eye size={11} /> {post.views || 0}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="px-2 py-2 border-t border-cream-100 grid grid-cols-4 gap-1">
        {REACTIONS.map((r) => {
          const active = myReaction === r.id;
          return (
            <button
              key={r.id}
              onClick={() => react(r.id)}
              data-testid={`post-react-${r.id}-${post.id}`}
              className={`inline-flex items-center justify-center gap-1.5 py-2 rounded-xl text-[12px] font-semibold transition-colors ${
                active ? "bg-pink-50 text-pink-600" : "text-plum-700/70 hover:bg-cream-50"
              }`}
            >
              <span aria-hidden="true" className="text-base">{r.emoji}</span>
              <span className="hidden sm:inline">{r.label}</span>
            </button>
          );
        })}
        <button
          onClick={() => setShowComments((v) => !v)}
          className="inline-flex items-center justify-center gap-1.5 py-2 rounded-xl text-[12px] font-semibold text-plum-700/70 hover:bg-cream-50"
          data-testid={`post-toggle-comments-${post.id}`}
        >
          <MessageCircle size={14} />
          <span className="hidden sm:inline">
            {commentsCount > 0 ? (showComments ? "Masquer" : `Voir (${commentsCount})`) : "Commenter"}
          </span>
        </button>
      </div>

      {/* Comments */}
      {showComments && (
        <div className="px-4 pb-4 pt-3 border-t border-cream-100 bg-cream-50/40 space-y-3" data-testid={`post-comments-${post.id}`}>
          {post.comments && post.comments.length > 0 && (
            <ul className="space-y-2">
              {post.comments.map((c) => (
                <li key={c.id} className="flex items-start gap-2">
                  <div className="w-7 h-7 rounded-full bg-plum-100 text-plum-700 grid place-items-center font-display font-extrabold text-[11px] shrink-0">
                    {(c.user_pseudo || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0 bg-white rounded-2xl px-3 py-2">
                    <div className="font-display font-bold text-plum-900 text-[12px]">{c.user_pseudo}</div>
                    <p className="text-[13px] text-plum-700/90 mt-0.5 leading-snug whitespace-pre-line">{renderMentionText(c.text)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {isAuthed ? (
            <MentionCommentForm
              postId={post.id}
              onSubmitted={onChange}
            />
          ) : (
            <Link to="/login" className="block text-center text-[12px] text-plum-700 hover:text-plum-900 font-semibold py-2 inline-flex items-center justify-center gap-1.5 w-full">
              <Lock size={11} /> Connectez-vous pour commenter
            </Link>
          )}
        </div>
      )}
    </article>
  );
}

// ---------- Daily summary (text + audio via OpenAI TTS) ----------
function DailySummaryCard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | playing | paused
  const [rate, setRate] = useState(1.0);
  const [bigText, setBigText] = useState(false);
  const audioRef = useRef(null);

  const ensureAudio = (b64) => {
    if (!b64) return null;
    if (!audioRef.current) {
      const a = new Audio(`data:audio/mpeg;base64,${b64}`);
      a.onended = () => setStatus("idle");
      a.onpause = () => setStatus((s) => (s === "playing" ? "paused" : s));
      a.onerror = () => { setStatus("idle"); setError("Lecture audio impossible."); };
      audioRef.current = a;
    }
    audioRef.current.playbackRate = rate;
    return audioRef.current;
  };

  const fetchSummary = async () => {
    if (summary) return summary;
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/posts/summary?include_audio=true");
      setSummary(r.data);
      return r.data;
    } catch (e) {
      setError(formatApiError(e) || "Impossible de générer le résumé pour le moment.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const onListen = async () => {
    if (status === "playing") {
      audioRef.current?.pause();
      return;
    }
    if (status === "paused") {
      audioRef.current?.play();
      setStatus("playing");
      return;
    }
    const data = await fetchSummary();
    if (!data) return;
    if (data.audio_base64) {
      const a = ensureAudio(data.audio_base64);
      a.play().then(() => setStatus("playing")).catch(() => setError("Lecture audio impossible."));
    } else if (data.text && "speechSynthesis" in window) {
      // Fallback : synthèse vocale navigateur
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(data.text);
      u.lang = "fr-FR";
      u.rate = rate;
      u.onend = () => setStatus("idle");
      window.speechSynthesis.speak(u);
      setStatus("playing");
    } else {
      setError("Audio indisponible. Lisez le résumé ci-dessous.");
    }
  };

  const onStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
    setStatus("idle");
  };

  // Update playback rate when slider changes
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = rate;
  }, [rate]);

  useEffect(() => () => {
    if (audioRef.current) audioRef.current.pause();
    if (typeof window !== "undefined" && "speechSynthesis" in window) window.speechSynthesis.cancel();
  }, []);

  return (
    <div className="phenix-card bg-gradient-to-br from-sun-50 to-pink-50 border-2 border-sun-200 p-5 sm:p-6 mb-4" data-testid="wall-summary-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-sun-400 text-plum-900 grid place-items-center shadow-md shrink-0">
          <Volume2 size={24} strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="phenix-eyebrow mb-1">Accessibilité · Audio</div>
          <h3 className="heading-display text-[1.2rem] sm:text-[1.4rem] text-plum-900 leading-tight">
            Écoutez le résumé de la journée des commerçants
          </h3>
          <p className="text-[13px] text-plum-700/75 mt-1">
            Un condensé écrit et audio en français, lisible en gros caractères.
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          onClick={onListen}
          disabled={loading}
          data-testid="summary-listen-btn"
          className="rounded-full bg-plum-700 hover:bg-plum-800 disabled:opacity-60 text-white h-12 px-5 inline-flex items-center gap-2 font-bold text-[14px] shadow-md shadow-plum-700/20"
        >
          {loading ? (
            <span className="inline-flex items-center gap-2"><span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> Préparation…</span>
          ) : status === "playing" ? (
            <><Pause size={16} /> Pause</>
          ) : status === "paused" ? (
            <><Play size={16} /> Reprendre</>
          ) : (
            <><Play size={16} /> Écouter le résumé</>
          )}
        </button>
        {status !== "idle" && (
          <button
            onClick={onStop}
            data-testid="summary-stop-btn"
            className="rounded-full bg-white hover:bg-cream-50 text-plum-700 border border-cream-200 h-12 w-12 grid place-items-center"
            aria-label="Arrêter"
          >
            <Square size={14} />
          </button>
        )}
        <div className="inline-flex items-center gap-2 bg-white rounded-full px-3 h-12 border border-cream-200 text-[12px] font-semibold text-plum-700">
          <span>Vitesse</span>
          <input
            type="range"
            min="0.7"
            max="1.3"
            step="0.05"
            value={rate}
            onChange={(e) => setRate(parseFloat(e.target.value))}
            className="w-20 accent-plum-700"
            data-testid="summary-rate"
            aria-label="Vitesse de lecture"
          />
          <span className="tabular-nums w-9 text-right">{rate.toFixed(2)}×</span>
        </div>
        <button
          onClick={() => setBigText((v) => !v)}
          data-testid="summary-bigtext-toggle"
          className={`rounded-full px-4 h-12 inline-flex items-center gap-1.5 text-[12px] font-bold transition-colors ${bigText ? "bg-plum-700 text-white" : "bg-white text-plum-700 border border-cream-200 hover:bg-cream-50"}`}
        >
          A+ Grand texte
        </button>
      </div>

      {/* Text */}
      {error && <p className="mt-4 text-[13px] text-pink-600" data-testid="summary-error">{error}</p>}
      {summary?.text && (
        <div className="mt-4 bg-white/85 rounded-2xl p-4 sm:p-5 border border-cream-200" data-testid="summary-text">
          <p className={`text-plum-900 leading-relaxed whitespace-pre-line ${bigText ? "text-[19px] sm:text-[21px] leading-[1.7]" : "text-[14px] sm:text-[15px]"}`}>
            {summary.text}
          </p>
          <div className="mt-2 text-[11px] text-plum-700/55">
            Résumé basé sur {summary.posts_count} publication{summary.posts_count > 1 ? "s" : ""} · généré à {new Date(summary.generated_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Embedded video / photo ----------
function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
}
function PostMedia({ video, photo }) {
  const ytId = getYouTubeId(video);
  if (ytId) {
    return (
      <div className="relative w-full aspect-video bg-black" data-testid="post-video-youtube">
        <iframe
          src={`https://www.youtube.com/embed/${ytId}?rel=0`}
          title="Vidéo commerçant"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
          className="absolute inset-0 w-full h-full"
        />
      </div>
    );
  }
  return (
    <video
      src={video}
      controls
      preload="metadata"
      poster={photo || undefined}
      className="w-full max-h-[480px] bg-black"
      data-testid="post-video-mp4"
    >
      Votre navigateur ne supporte pas la lecture vidéo.
    </video>
  );
}



// ---------- Cached merchants list (module-level singleton) ----------
let _merchantsCache = null;
let _merchantsPromise = null;
function loadMerchantsOnce() {
  if (_merchantsCache) return Promise.resolve(_merchantsCache);
  if (_merchantsPromise) return _merchantsPromise;
  _merchantsPromise = api.get("/public/merchants-mentionable")
    .then((r) => { _merchantsCache = r.data || []; return _merchantsCache; })
    .catch(() => { _merchantsCache = []; return []; });
  return _merchantsPromise;
}

function MentionCommentForm({ postId, onSubmitted }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [suggest, setSuggest] = useState({ open: false, items: [], start: 0, end: 0, active: 0 });
  const ref = useRef(null);

  useEffect(() => { loadMerchantsOnce().then(setMerchants); }, []);

  const updateSuggest = (value, caret) => {
    // Find an @token whose end equals caret position
    const before = value.slice(0, caret);
    const m = before.match(/@([A-Za-z0-9_\u00C0-\u017F.-]{0,40})$/);
    if (!m) return setSuggest({ open: false, items: [], start: 0, end: 0, active: 0 });
    const q = m[1].toLowerCase();
    const items = merchants
      .filter((mer) => (mer.pseudo || "").toLowerCase().includes(q) || (mer.merchant_name || "").toLowerCase().includes(q))
      .slice(0, 5);
    if (!items.length) return setSuggest({ open: false, items: [], start: 0, end: 0, active: 0 });
    setSuggest({ open: true, items, start: caret - m[0].length, end: caret, active: 0 });
  };

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    updateSuggest(v, e.target.selectionStart || v.length);
  };

  const choose = (mer) => {
    const insert = `@${mer.pseudo} `;
    const next = text.slice(0, suggest.start) + insert + text.slice(suggest.end);
    setText(next);
    setSuggest({ open: false, items: [], start: 0, end: 0, active: 0 });
    requestAnimationFrame(() => {
      if (ref.current) {
        const pos = suggest.start + insert.length;
        ref.current.focus();
        ref.current.setSelectionRange(pos, pos);
      }
    });
  };

  const onKeyDown = (e) => {
    if (!suggest.open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setSuggest((s) => ({ ...s, active: (s.active + 1) % s.items.length })); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSuggest((s) => ({ ...s, active: (s.active - 1 + s.items.length) % s.items.length })); }
    else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); choose(suggest.items[suggest.active]); }
    else if (e.key === "Escape") { setSuggest({ open: false, items: [], start: 0, end: 0, active: 0 }); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/posts/${postId}/comment`, { text });
      window.dispatchEvent(new CustomEvent("istres-action-completed", { detail: { kind: "comment" } }));
      setText("");
      setSuggest({ open: false, items: [], start: 0, end: 0, active: 0 });
      onSubmitted();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="relative flex gap-2 items-end" data-testid={`post-comment-form-${postId}`}>
      <div className="flex-1 relative">
        <Textarea
          ref={ref}
          value={text}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder="Commentaire bienveillant — tapez @ pour mentionner un commerçant"
          rows={1}
          className="rounded-2xl bg-white text-[13px] min-h-[40px] resize-none pr-9"
          data-testid={`post-comment-input-${postId}`}
        />
        <AtSign size={13} className="absolute right-3 bottom-3 text-plum-400 pointer-events-none" />
        {suggest.open && (
          <ul
            className="absolute z-30 left-0 right-0 bottom-full mb-1 bg-white border border-cream-200 rounded-2xl shadow-lg overflow-hidden"
            data-testid={`mention-suggest-${postId}`}
          >
            {suggest.items.map((m, idx) => (
              <li key={m.id}>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); choose(m); }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] ${idx === suggest.active ? "bg-pink-50" : "hover:bg-cream-50"}`}
                  data-testid={`mention-option-${m.pseudo}`}
                >
                  <span className="w-7 h-7 rounded-full bg-sun-100 text-sun-700 grid place-items-center font-display font-extrabold text-[11px] shrink-0">
                    {(m.pseudo || "?").charAt(0).toUpperCase()}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-display font-bold text-plum-900 text-[12px] truncate">@{m.pseudo}</span>
                    <span className="block text-[11px] text-plum-700/65 truncate">{m.merchant_name}{m.merchant_category ? ` · ${m.merchant_category}` : ""}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Button type="submit" disabled={busy || !text.trim()} data-testid={`post-comment-submit-${postId}`} className="rounded-full bg-plum-700 hover:bg-plum-800 text-white h-10 px-3 shrink-0">
        <Send size={13} />
      </Button>
    </form>
  );
}


function ComposePostDialog({ open, onClose }) {
  const [text, setText] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [videoMode, setVideoMode] = useState("none"); // "none" | "url" | "file"
  const [busy, setBusy] = useState(false);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) {
      toast.error("Photo trop lourde (max 4 Mo). Compressez-la avant l'envoi.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPhotoUrl(reader.result);
    reader.readAsDataURL(f);
  };

  const onVideoFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) {
      toast.error("Vidéo trop lourde (max 12 Mo). Préférez un lien YouTube pour les vidéos plus longues.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setVideoUrl(reader.result);
    reader.readAsDataURL(f);
  };

  const reset = () => {
    setText("");
    setPhotoUrl("");
    setVideoUrl("");
    setVideoMode("none");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return toast.error("Ajoutez un texte à votre publication.");
    if (text.trim().length < 4) return toast.error("Votre message est trop court.");
    setBusy(true);
    try {
      await api.post("/posts/", {
        text: text.trim(),
        photo_url: photoUrl || "",
        video_url: videoUrl || "",
      });
      toast.success("Post publié ✓");
      reset();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e) || "Impossible de publier — vérifiez votre connexion.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-cream-50 border-cream-200 rounded-2xl" data-testid="compose-post-dialog">
        <DialogHeader>
          <DialogTitle className="heading-display text-[1.3rem] text-plum-900">Nouveau post</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Quoi de neuf chez vous ? (4 caractères minimum)"
            rows={4}
            className="rounded-xl bg-white"
            data-testid="post-text"
          />

          {/* Media options */}
          <div className="flex flex-wrap items-center gap-2">
            <label className="rounded-full bg-plum-50 hover:bg-plum-100 text-plum-700 px-3 py-2 text-[12px] font-bold inline-flex items-center gap-1.5 cursor-pointer">
              <ImageIcon size={13} /> Photo
              <input type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="post-photo" />
            </label>
            <button
              type="button"
              onClick={() => setVideoMode((m) => (m === "url" ? "none" : "url"))}
              data-testid="post-video-url-toggle"
              className={`rounded-full px-3 py-2 text-[12px] font-bold inline-flex items-center gap-1.5 transition-colors ${videoMode === "url" ? "bg-plum-700 text-white" : "bg-plum-50 hover:bg-plum-100 text-plum-700"}`}
            >
              <Play size={12} /> Lien vidéo (YouTube)
            </button>
            <label className="rounded-full bg-plum-50 hover:bg-plum-100 text-plum-700 px-3 py-2 text-[12px] font-bold inline-flex items-center gap-1.5 cursor-pointer">
              <Play size={12} /> Fichier MP4
              <input type="file" accept="video/mp4,video/quicktime,video/webm" onChange={(e) => { setVideoMode("file"); onVideoFile(e); }} className="hidden" data-testid="post-video-file" />
            </label>
            {photoUrl && <span className="text-[11px] text-mint-600">✓ Photo prête</span>}
            {videoUrl && <span className="text-[11px] text-mint-600">✓ Vidéo prête</span>}
          </div>

          {videoMode === "url" && (
            <input
              value={videoUrl.startsWith("data:") ? "" : videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="Collez un lien YouTube — ex. https://youtu.be/abc123"
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-cream-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-plum-200"
              data-testid="post-video-url-input"
            />
          )}

          {photoUrl && <img src={photoUrl} alt="" className="rounded-xl max-h-48 object-cover w-full" />}
          {videoUrl && videoUrl.startsWith("data:") && (
            <video src={videoUrl} controls className="rounded-xl max-h-56 w-full bg-black" />
          )}

          <Button type="submit" disabled={busy || !text.trim()} data-testid="post-submit" className="rounded-full bg-plum-700 hover:bg-plum-800 disabled:opacity-60 text-white w-full h-12 font-bold">
            {busy ? "Publication…" : "Publier sur le mur"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
 * Attached event card with nearest parking
 * ============================================================ */
function AttachedEventCard({ ev, postId }) {
  if (!ev) return null;
  let dateLabel = "";
  try {
    const d = new Date(ev.start_at);
    const day = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
    const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    dateLabel = `${day.charAt(0).toUpperCase() + day.slice(1)} · ${time}`;
  } catch (_) {
    dateLabel = ev.start_at || "";
  }
  const parking = ev.nearest_parking;
  const directionsUrl = parking
    ? `https://www.google.com/maps/dir/?api=1&destination=${parking.lat},${parking.lng}&destination_place_id=${encodeURIComponent(parking.name)}`
    : null;

  return (
    <div
      className="mx-4 mb-3 rounded-2xl border border-plum-200 bg-gradient-to-br from-plum-50 to-cream-50 overflow-hidden"
      data-testid={`post-attached-event-${postId || ev.id}`}
    >
      <div className="flex items-start gap-3 p-3.5">
        <div className="w-11 h-11 rounded-xl bg-plum-700 text-white grid place-items-center shrink-0 shadow-md">
          <Calendar size={20} strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wider text-pink-600 mb-0.5">
            📅 Événement à l&apos;agenda
          </div>
          <div className="font-display font-bold text-[14px] text-plum-900 leading-tight">{ev.title}</div>
          <div className="text-[12px] text-plum-700/80 mt-1 flex items-center gap-1.5">
            <Calendar size={11} strokeWidth={2.3} />
            <span>{dateLabel}</span>
          </div>
          {ev.location && (
            <div className="text-[12px] text-plum-700/80 mt-0.5 flex items-start gap-1.5">
              <MapPin size={11} strokeWidth={2.3} className="mt-0.5 shrink-0" />
              <span className="line-clamp-1">{ev.location}</span>
            </div>
          )}
        </div>
        <Link
          to="/agenda"
          className="shrink-0 inline-flex items-center gap-1 rounded-full bg-plum-700 hover:bg-plum-800 text-white px-3 h-8 text-[12px] font-bold transition-colors"
          data-testid={`event-agenda-link-${ev.id}`}
        >
          Agenda <ArrowRight size={12} />
        </Link>
      </div>
      {parking && (
        <a
          href={directionsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2.5 border-t border-plum-200 bg-white/60 hover:bg-white text-[12px] transition-colors group"
          data-testid={`event-parking-${ev.id}`}
        >
          <div className="w-7 h-7 rounded-lg bg-mint-100 text-mint-700 grid place-items-center shrink-0">
            <Car size={13} strokeWidth={2.3} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-plum-900 text-[12px] truncate">
              🅿️ {parking.name}
            </div>
            <div className="text-[11px] text-plum-700/65">
              à {parking.distance_m < 1000 ? `${parking.distance_m} m` : `${(parking.distance_m / 1000).toFixed(1)} km`} · {parking.type} · {parking.hours}
            </div>
          </div>
          <span className="text-[11px] font-bold text-plum-700 group-hover:text-pink-500 transition-colors shrink-0">Y aller →</span>
        </a>
      )}
    </div>
  );
}

